import { useQuery } from "@tanstack/react-query";
import type { EmblaCarouselType, EmblaEventType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useCallback, useEffect, useRef } from "react";
import { useMedia } from "react-use";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ScaffoldSection,
  ScaffoldSectionContainer,
  ScaffoldSectionContent,
  ScaffoldSectionHeader,
  ScaffoldSectionTitle,
} from "./scaffold";

/** Event as returned by the TOMA ticket shop website API (v1, only the fields used here). */
type ShopEvent = {
  slug: string;
  title: string;
  whenLine: string;
  imageUrl: string | null;
  url: string;
  theme: { bg: string; text: string; primary: string };
};

const TWEEN_FACTOR_BASE = 0.2;

const numberWithinRange = (number: number, min: number, max: number): number =>
  Math.min(Math.max(number, min), max);

/**
 * Public, read-only JSON API of the ticket shop (CORS *). The workers.dev address stays
 * enabled after the move to buchen.toma-events.de; the event links (`url`) always point
 * to the shop's current public address.
 */
const TICKET_SHOP_API =
  "https://toma-tickets.buchhaltung-02a.workers.dev/api/v1/events";
const MAX_EVENTS = 6;

/**
 * Same rule as the shop (src/shared/media.ts): built-in cards and uploads marked as cut-out
 * are transparent PNG/WebP posters and are shown free-standing, without a card background.
 */
const CUTOUT_PATH = /\/cards\/|-cutout\.(png|webp|gif)$/;
const isCutout = (url: string) => CUTOUT_PATH.test(new URL(url).pathname);

async function fetchEvents(): Promise<ShopEvent[]> {
  const response = await fetch(TICKET_SHOP_API);
  if (!response.ok) {
    throw new Error(`Ticket shop API: ${response.status}`);
  }
  const { events } = (await response.json()) as { events: ShopEvent[] };
  return events.slice(0, MAX_EVENTS);
}

export function News() {
  const isMd = useMedia("(min-width: 768px)");
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    // Keep one or two cards centred instead of pinned to the left edge
    containScroll: false,
  });
  const tweenFactor = useRef(0);
  const tweenNodes = useRef<HTMLElement[]>([]);

  const { data: events = [], isLoading } = useQuery({
    queryKey: ["ticket-shop-events"],
    queryFn: fetchEvents,
  });

  const setTweenNodes = useCallback((emblaApi: EmblaCarouselType): void => {
    tweenNodes.current = emblaApi
      .slideNodes()
      .map(
        (slideNode) =>
          slideNode.querySelector(".embla__slide__number") as HTMLElement
      );
  }, []);

  const setTweenFactor = useCallback((emblaApi: EmblaCarouselType) => {
    tweenFactor.current = TWEEN_FACTOR_BASE * emblaApi.scrollSnapList().length;
  }, []);

  const tweenScale = useCallback(
    (emblaApi: EmblaCarouselType, eventName?: EmblaEventType) => {
      const engine = emblaApi.internalEngine();
      const scrollProgress = emblaApi.scrollProgress();
      const slidesInView = emblaApi.slidesInView();
      const isScrollEvent = eventName === "scroll";

      emblaApi.scrollSnapList().forEach((scrollSnap, snapIndex) => {
        let diffToTarget = scrollSnap - scrollProgress;
        const slidesInSnap = engine.slideRegistry[snapIndex];

        slidesInSnap.forEach((slideIndex) => {
          if (isScrollEvent && !slidesInView.includes(slideIndex)) return;

          if (engine.options.loop) {
            engine.slideLooper.loopPoints.forEach((loopItem) => {
              const target = loopItem.target();

              if (slideIndex === loopItem.index && target !== 0) {
                const sign = Math.sign(target);

                if (sign === -1) {
                  diffToTarget = scrollSnap - (1 + scrollProgress);
                }
                if (sign === 1) {
                  diffToTarget = scrollSnap + (1 - scrollProgress);
                }
              }
            });
          }

          const tweenValue = 1 - Math.abs(diffToTarget * tweenFactor.current);
          const scale = numberWithinRange(tweenValue, 0, 1).toString();
          const tweenNode = tweenNodes.current[slideIndex];
          if (tweenNode) {
            tweenNode.style.transform = `scale(${scale})`;
          }
        });
      });
    },
    []
  );

  useEffect(() => {
    if (!emblaApi) return;

    setTweenNodes(emblaApi);

    emblaApi.on("reInit", setTweenNodes);

    if (!isMd) {
      setTweenFactor(emblaApi);
      tweenScale(emblaApi);

      emblaApi
        .on("reInit", setTweenFactor)
        .on("reInit", tweenScale)
        .on("scroll", tweenScale)
        .on("slideFocus", tweenScale);
    }
  }, [emblaApi, isMd, setTweenFactor, setTweenNodes, tweenScale]);

  // Nothing on sale → hide the section instead of showing an empty carousel
  if (!isLoading && events.length === 0) {
    return null;
  }

  return (
    <ScaffoldSection id="events">
      <ScaffoldSectionContainer className="px-0 md:px-4">
        <ScaffoldSectionHeader className="px-4">
          <ScaffoldSectionTitle>News & Events</ScaffoldSectionTitle>
        </ScaffoldSectionHeader>

        <ScaffoldSectionContent>
          <div className="mx-auto w-full max-w-[48rem] [--slide-height:19rem] [--slide-size:70%] [--slide-spacing:0rem] md:max-w-full md:[--slide-size:40%] md:[--slide-spacing:0rem] lg:[--slide-size:30%]">
            <div className="overflow-hidden" ref={emblaRef}>
              <div className="-ml-[var(--slide-spacing)] flex touch-pan-y touch-pinch-zoom">
                {isLoading
                  ? Array.from(
                      { length: 3 },
                      (_, index) => `skeleton-${index}`
                    ).map((key) => (
                      <div
                        className="translate-z-0 min-w-0 flex-[0_0_var(--slide-size)] translate-x-0 translate-y-0 pl-[var(--slide-spacing)]"
                        key={key}
                      >
                        <Skeleton className="aspect-[1/1.4] rounded-xl" />
                      </div>
                    ))
                  : events.map((event) => (
                      <a
                        className="translate-z-0 min-w-0 flex-[0_0_var(--slide-size)] translate-x-0 translate-y-0 pl-[var(--slide-spacing)]"
                        href={event.url}
                        key={event.slug}
                      >
                        {event.imageUrl && isCutout(event.imageUrl) ? (
                          <div className="embla__slide__number backface-visibility-hidden relative flex aspect-[1/1.4] select-none items-center justify-center">
                            <img
                              alt={event.title}
                              className="size-full object-contain object-center drop-shadow-[0_16px_22px_rgb(0_0_0/0.35)]"
                              height={500}
                              loading="lazy"
                              src={event.imageUrl}
                              width={360}
                            />
                          </div>
                        ) : (
                          <div
                            className="embla__slide__number backface-visibility-hidden relative flex aspect-[1/1.4] select-none flex-col items-center justify-center gap-2 overflow-hidden rounded-xl p-6 text-center shadow-[inset_0_0_0_0.2rem_var(--detail-medium-contrast)]"
                            style={{
                              background: event.theme.bg,
                              color: event.theme.text,
                            }}
                          >
                            {event.imageUrl ? (
                              <img
                                alt={event.title}
                                className="absolute inset-0 size-full object-cover object-center"
                                height={500}
                                loading="lazy"
                                src={event.imageUrl}
                                width={360}
                              />
                            ) : (
                              <>
                                <span
                                  className="font-bold text-3xl leading-tight"
                                  style={{ color: event.theme.primary }}
                                >
                                  {event.title}
                                </span>
                                <span className="text-base">
                                  {event.whenLine}
                                </span>
                              </>
                            )}
                          </div>
                        )}
                      </a>
                    ))}
              </div>
            </div>

            <div className="mx-auto mt-7 flex w-fit gap-2">
              <Button
                aria-label="Vorherige Seite"
                onClick={() => emblaApi?.scrollPrev()}
                size="icon"
                variant="outline"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </Button>
              <Button
                aria-label="Nächste Seite"
                onClick={() => emblaApi?.scrollNext()}
                size="icon"
                variant="outline"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </ScaffoldSectionContent>
      </ScaffoldSectionContainer>
    </ScaffoldSection>
  );
}
